import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "../constants";
import ImageUpload from "../components/ImageUpload";
import { useFormData, useVehicleMakes } from "../hooks";
import Logo from '../assets/Logo.jpg';

// Diagnostic Result View Component
const DiagnosticResultView = ({ result, onBackToForm }) => {
  if (!result || !result.diagnostic_result) return null;

  const { diagnostic_result } = result;

  const urgencyColors = {
    low: "text-green-600 bg-green-50 border-green-200",
    medium: "text-yellow-600 bg-yellow-50 border-yellow-200",
    high: "text-orange-600 bg-orange-50 border-orange-200",
    critical: "text-red-600 bg-red-50 border-red-200",
  };

  const urgencyColor =
    urgencyColors[diagnostic_result.urgency?.toLowerCase()] ||
    urgencyColors.medium;

  const safeToDriveColors = {
    yes: "text-green-600 bg-green-50 border-green-200",
    no: "text-red-600 bg-red-50 border-red-200",
    maybe: "text-yellow-600 bg-yellow-50 border-yellow-200",
  };

  const safeToDriveColor =
    safeToDriveColors[diagnostic_result.safe_to_drive?.toLowerCase()] ||
    safeToDriveColors.maybe;

  return (
    <div className="min-h-screen bg-orange-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-orange-900 mb-2">
                🔧 Automotive Diagnostic Report
              </h1>
              <p className="text-orange-700">
                Complete analysis of your vehicle's condition
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onBackToForm}
                className={`px-6 py-3 text-white rounded-lg transition-colors duration-200 flex items-center gap-2 ${
                  isFormValid() && !loading
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Form
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className={`px-6 py-3 text-white rounded-lg transition-colors duration-200 flex items-center gap-2 ${
                  isFormValid() && !loading
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Home
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Vehicle Information - Professional Format */}
          <div className="bg-white rounded-xl p-6 border border-orange-200 shadow-sm">
            <h3 className="text-xl font-semibold text-orange-900 mb-4 flex items-center">
              <span className="mr-2 text-orange-600">🚗</span>
              Vehicle Information
            </h3>

            {/* Main Vehicle Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-800">
                  {diagnostic_result.vehicle?.year || "Year"}{" "}
                  {diagnostic_result.vehicle?.make || "Make"}{" "}
                  {diagnostic_result.vehicle?.model || "Model"}
                  {diagnostic_result.vehicle?.engine &&
                    ` (${diagnostic_result.vehicle.engine})`}
                </h4>
                {diagnostic_result.vehicle?.vin && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(
                        diagnostic_result.vehicle.vin
                      );
                      // You could add a toast notification here
                    }}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full hover:bg-gray-200 transition-colors duration-200"
                  >
                    📋 Copy VIN
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">VIN:</span>
                  <p className="text-gray-800 font-mono text-xs break-all">
                    {diagnostic_result.vehicle?.vin || "Not detected"}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Built:</span>
                  <p className="text-gray-800">
                    {diagnostic_result.vehicle?.assembly_plant &&
                      diagnostic_result.vehicle?.country_of_origin
                      ? `${diagnostic_result.vehicle.assembly_plant} – ${diagnostic_result.vehicle.country_of_origin}`
                      : "Not detected"}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Age:</span>
                  <p className="text-gray-800">
                    {diagnostic_result.vehicle?.vehicle_age_years
                      ? `${diagnostic_result.vehicle.vehicle_age_years} years`
                      : "Not detected"}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Status:</span>
                  <p className="text-gray-800 capitalize">
                    {diagnostic_result.vehicle?.vin_status || "Unknown"}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Mileage:</span>
                  <p className="text-gray-800 capitalize">
                    {diagnostic_result.vehicle?.mileage
                      ? `${diagnostic_result.vehicle.mileage} mi`
                      : "Unknown"}
                  </p>
                </div>

                <div>
                  <span className="font-medium text-gray-600">
                    Mileage per year:
                  </span>
                  <p className="text-gray-800 capitalize">
                    {diagnostic_result.vehicle?.mileage_per_year
                      ? `${diagnostic_result.vehicle.mileage_per_year} mi/year`
                      : "Unknown"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Alerts Summary - Professional Format */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2 text-gray-600">⚠️</span>
              Dashboard Alerts
            </h3>

            {/* Alerts List */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <h4 className="font-semibold text-gray-700 mb-3">
                Detected Alerts:
              </h4>
              <div className="space-y-2">
                {diagnostic_result.dashboard_findings?.map((warning, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 bg-white rounded-lg border border-gray-200"
                  >
                    <span className="text-gray-500 mr-3 text-lg">•</span>
                    <span className="text-gray-800">{warning}</span>
                  </div>
                )) || (
                    <p className="text-gray-600 italic">
                      No specific warnings detected
                    </p>
                  )}
              </div>
            </div>

            {/* Symptoms Summary */}
            {diagnostic_result.symptoms_summary && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-2">Summary:</h4>
                <p className="text-gray-800 leading-relaxed">
                  {diagnostic_result.symptoms_summary}
                </p>
              </div>
            )}
          </div>

          {/* Diagnosis - Professional Format */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2 text-gray-600">🔧</span>
              Diagnosis
            </h3>

            {/* Immediate Concerns */}
            {diagnostic_result.diagnosis?.immediate_concerns &&
              diagnostic_result.diagnosis.immediate_concerns.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Immediate Concerns:
                  </h4>
                  <div className="space-y-2">
                    {diagnostic_result.diagnosis.immediate_concerns.map(
                      (concern, index) => (
                        <div
                          key={index}
                          className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="text-red-600 mr-3 mt-1">•</span>
                          <span className="text-gray-800">{concern}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* General Possible Causes Table */}
            {diagnostic_result.diagnosis?.general_possible_causes &&
              diagnostic_result.diagnosis.general_possible_causes.length >
              0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Possible Causes:
                  </h4>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    <div className="space-y-1">
                      {diagnostic_result.diagnosis.general_possible_causes.map(
                        (cause, index) => (
                          <div
                            key={index}
                            className="flex items-center p-3 bg-white border-b border-gray-100 last:border-b-0"
                          >
                            <span className="text-gray-500 mr-3 text-lg">
                              •
                            </span>
                            <span className="text-gray-800 flex-1">
                              {cause}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Known Model Issues */}
            {diagnostic_result.diagnosis?.known_model_issues &&
              diagnostic_result.diagnosis.known_model_issues.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Known Model Issues:
                  </h4>
                  <div className="space-y-3">
                    {diagnostic_result.diagnosis.known_model_issues.map(
                      (issue, index) => (
                        <div
                          key={index}
                          className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-semibold text-gray-800">
                              {issue.issue}
                            </h5>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${issue.likelihood === "high"
                                ? "bg-red-100 text-red-800"
                                : issue.likelihood === "medium"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-green-100 text-green-800"
                                }`}
                            >
                              {issue.likelihood} probability
                            </span>
                          </div>
                          <p className="text-gray-700 mb-2">
                            {issue.description}
                          </p>
                          <div className="grid grid-cols-1 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-600">
                                Affected Years:
                              </span>
                              <p className="text-gray-800">
                                {issue.affected_years}
                              </p>
                            </div>
                            {issue.technical_details && (
                              <div>
                                <span className="font-medium text-gray-600">
                                  Technical Details:
                                </span>
                                <p className="text-gray-800">
                                  {issue.technical_details}
                                </p>
                              </div>
                            )}
                          </div>
                          {issue.symptoms && (
                            <div className="mt-2">
                              <span className="font-medium text-gray-600">
                                Symptoms:
                              </span>
                              <p className="text-gray-800">{issue.symptoms}</p>
                            </div>
                          )}
                          {issue.recall_tsb && (
                            <div className="mt-2">
                              <span className="font-medium text-gray-600">
                                Recall/TSB:
                              </span>
                              <p className="text-gray-800">
                                {issue.recall_tsb}
                              </p>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Age Related Issues */}
            {diagnostic_result.diagnosis?.age_related_issues &&
              diagnostic_result.diagnosis.age_related_issues.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Age-Related Issues:
                  </h4>
                  <div className="space-y-2">
                    {diagnostic_result.diagnosis.age_related_issues.map(
                      (issue, index) => (
                        <div
                          key={index}
                          className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="text-gray-500 mr-3 mt-1">•</span>
                          <span className="text-gray-800">{issue}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Drive Safety - Professional Format */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2 text-gray-600">🛡️</span>
              Drive Safety
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold capitalize">
                  Drive Status:{" "}
                  {diagnostic_result.safe_to_drive === "yes"
                    ? "✅ Safe"
                    : diagnostic_result.safe_to_drive === "no"
                      ? "❌ Not Safe"
                      : "⚠️ Limited use recommended"}
                </span>
                <div
                  className={`w-4 h-4 rounded-full ${diagnostic_result.safe_to_drive === "yes"
                    ? "bg-green-500"
                    : diagnostic_result.safe_to_drive === "no"
                      ? "bg-red-500"
                      : "bg-yellow-500"
                    }`}
                ></div>
              </div>
              <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                {diagnostic_result.safe_to_drive_details}
              </p>
            </div>
          </div>

          {/* Recommended Actions - Professional Format */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2 text-gray-600">🔧</span>
              Recommended Actions
            </h3>

            {/* Immediate Steps */}
            {diagnostic_result.recommended_action?.immediate_steps &&
              diagnostic_result.recommended_action.immediate_steps.length >
              0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Immediate Steps:
                  </h4>
                  <div className="space-y-2">
                    {diagnostic_result.recommended_action.immediate_steps.map(
                      (step, index) => (
                        <div
                          key={index}
                          className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="text-red-600 mr-3 mt-1 text-lg">
                            {index + 1}.
                          </span>
                          <span className="text-gray-800">{step}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Short Term */}
            {diagnostic_result.recommended_action?.short_term &&
              diagnostic_result.recommended_action.short_term.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Short Term:
                  </h4>
                  <div className="space-y-2">
                    {diagnostic_result.recommended_action.short_term.map(
                      (action, index) => (
                        <div
                          key={index}
                          className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="text-gray-500 mr-3 mt-1 text-lg">
                            •
                          </span>
                          <span className="text-gray-800">{action}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Long Term */}
            {diagnostic_result.recommended_action?.long_term &&
              diagnostic_result.recommended_action.long_term.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Long Term:
                  </h4>
                  <div className="space-y-2">
                    {diagnostic_result.recommended_action.long_term.map(
                      (action, index) => (
                        <div
                          key={index}
                          className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="text-gray-500 mr-3 mt-1 text-lg">
                            •
                          </span>
                          <span className="text-gray-800">{action}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <span className="font-medium text-gray-600">DIY Possible:</span>
                <p className="text-gray-800 capitalize mt-1">
                  {diagnostic_result.recommended_action?.diy_possible === "yes"
                    ? "✅ Yes"
                    : diagnostic_result.recommended_action?.diy_possible ===
                      "no"
                      ? "❌ No"
                      : diagnostic_result.recommended_action?.diy_possible ===
                        "partial"
                        ? "⚠️ Partial"
                        : "Unknown"}
                </p>
              </div>
            </div>
          </div>

          {/* Urgency Level - Professional Format */}
          <div
            className={`rounded-xl p-6 border border-gray-200 shadow-sm ${urgencyColor}`}
          >
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <span className="mr-2 text-gray-600">🚨</span>
              Urgency Level
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold capitalize">
                  {diagnostic_result.urgency}
                </span>
                <div
                  className={`w-6 h-6 rounded-full ${diagnostic_result.urgency === "critical"
                    ? "bg-red-500"
                    : diagnostic_result.urgency === "high"
                      ? "bg-orange-500"
                      : diagnostic_result.urgency === "medium"
                        ? "bg-yellow-500"
                        : "bg-green-500"
                    }`}
                ></div>
              </div>
              <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                {diagnostic_result.urgency_explanation}
              </p>
              {diagnostic_result.additional_notes && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-800">
                    {diagnostic_result.additional_notes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Preventive Tips */}
          {diagnostic_result.additional_context?.preventive_advice && (
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2 text-gray-600">💡</span>
                Preventive Tips
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-gray-800 leading-relaxed">
                  {diagnostic_result.additional_context.preventive_advice}
                </p>
              </div>
            </div>
          )}

          {/* Additional Context */}
          {diagnostic_result.additional_context && (
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2 text-gray-600">ℹ️</span>
                Additional Context
              </h3>

              {diagnostic_result.additional_context.common_failure_points && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">
                    Common Failure Points:
                  </h4>
                  <p className="text-gray-800">
                    {diagnostic_result.additional_context.common_failure_points}
                  </p>
                </div>
              )}

              {diagnostic_result.additional_context
                .maintenance_history_questions && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 mb-2">
                      Maintenance History Questions:
                    </h4>
                    <div className="space-y-2">
                      {diagnostic_result.additional_context.maintenance_history_questions.map(
                        (question, index) => (
                          <div
                            key={index}
                            className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <span className="text-gray-500 mr-3 mt-1">•</span>
                            <span className="text-gray-800">{question}</span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {diagnostic_result.additional_context.preventive_advice && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">
                    Preventive Advice:
                  </h4>
                  <p className="text-gray-800">
                    {diagnostic_result.additional_context.preventive_advice}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Processed Images - Professional Format */}
          {result.s3_links && result.s3_links.length > 0 && (
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2 text-gray-600">📸</span>
                Processed Photos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.s3_links.map((link, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <img
                      src={link}
                      alt={`Processed image ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <p className="text-sm text-gray-600 mt-2 font-medium">
                      {index === 0
                        ? "Vehicle Identification"
                        : index === 1
                          ? "Dashboard Analysis"
                          : `Image ${index + 1}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2 text-gray-600">ℹ️</span>
              Additional Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">
                  Confidence Level:
                </span>
                <p className="text-gray-800 capitalize">
                  {diagnostic_result.confidence_level || "Unknown"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">
                  Problem Domain:
                </span>
                <p className="text-gray-800 capitalize">
                  {diagnostic_result.problem_domain || "Unknown"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function DiagnosticResult() {
  const navigate = useNavigate();
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Scroll to top when result is shown
  useEffect(() => {
    if (showResult) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showResult]);

  const {
    dashboardImage,
    vinImage,
    description,
    name,
    email,
    phone,
    vehicleMake,
    vehicleModel,
    vehicleYear,
    updateDashboardImage,
    updateVinImage,
    updateDescription,
    updateName,
    updateEmail,
    updatePhone,
    updateVehicleMake,
    updateVehicleModel,
    updateVehicleYear,
    isFormValid,
  } = useFormData();

  const { makes, loading: makesLoading, error: makesError } = useVehicleMakes();

  const analyze = async (
    dashboardImage,
    vinImage,
    description,
    name,
    email,
    phone,
    vehicleMake,
    vehicleModel,
    vehicleYear
  ) => {
    setLoading(true);
    setError(null);
    setAnalysisResult(null);

    try {
      // Validate inputs - ahora es más flexible
      if (
        !description?.trim() ||
        !name?.trim() ||
        !phone?.trim()
      ) {
        throw new Error("Please complete all required fields");
      }

      // Validar que tengamos al menos información del vehículo O imágenes
      const hasVehicleInfo = vehicleMake?.trim() && vehicleModel?.trim() && vehicleYear?.trim();
      const hasImages = dashboardImage || vinImage;
      
      if (!hasVehicleInfo && !hasImages) {
        throw new Error("Please provide either vehicle information (Make, Model, Year) or upload at least one image");
      }

      // Create FormData
      const formData = new FormData();
      
      // Agregar imágenes solo si existen
      if (dashboardImage) {
        formData.append("images", dashboardImage);
      }
      if (vinImage) {
        formData.append("images", vinImage);
      }
      
      formData.append("user_description", description);
      formData.append("customer_name", name);
      formData.append("customer_email", email || "");
      formData.append("customer_phone_number", phone);
      formData.append("user_id", "react_frontend_user");
      
      // Agregar información del vehículo si está disponible
      if (hasVehicleInfo) {
        formData.append("vehicle_make", vehicleMake);
        formData.append("vehicle_model", vehicleModel);
        formData.append("vehicle_year", vehicleYear);
      }

      // Real API call to the backend
      const response = await fetch("https://api.azucarai.com/dash_ai", {
        //http://localhost:8000/dash_ai
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.content.success !== false) {
        setAnalysisResult(result.content);
        setShowResult(true);
        return result.content;
      } else {
        throw new Error(result.content.message || "Analysis error");
      }
    } catch (err) {
      setError(err.message || "Error analyzing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    await analyze(dashboardImage, vinImage, description, name, email, phone, vehicleMake, vehicleModel, vehicleYear);
  };

  const handleNewAnalysis = () => {
    updateDashboardImage(null);
    updateVinImage(null);
    updateDescription("");
    updateName("");
    updateEmail("");
    updatePhone("");
    updateVehicleMake("");
    updateVehicleModel("");
    updateVehicleYear("");
    setAnalysisResult(null);
    setError(null);
    setShowResult(false);
    setEmailError("");
  };

  const handleBackToForm = () => {
    setShowResult(false);
    setAnalysisResult(null);
    setError(null);
    // Reset all form fields using the hook's resetForm function
    updateDashboardImage(null);
    updateVinImage(null);
    updateDescription("");
    updateName("");
    updateEmail("");
    updatePhone("");
    updateVehicleMake("");
    updateVehicleModel("");
    updateVehicleYear("");
    setEmailError("");
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() && !emailRegex.test(email.trim())) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  // Show result view if we have a result
  if (showResult && analysisResult) {
    return (
      <DiagnosticResultView
        result={analysisResult}
        onBackToForm={handleBackToForm}
      />
    );
  }

  // Show main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-2 sm:px-6 lg:px-8 py-2 lg:py-12">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 lg:px-8 py-8 text-white">
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-0 items-center justify-between">
              <div className="flex items-start w-full lg:w-auto">
                <button
                  onClick={handleBackToHome}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-all duration-200"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Back to Home
                </button>
              </div>
              <div className="text-center w-full lg:w-auto">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  {APP_CONFIG.description}
                </h1>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-2 md:p-12">

            <div className="space-y-8">
              {/* Personal Information */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => updateName(e.target.value)}
                      placeholder="Enter your full name"
                      className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
                      required
                    />
                  </div>

                  {/* Phone Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => updatePhone(e.target.value)}
                      placeholder="Enter your phone number"
                      className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
                      required
                    />
                  </div>

                  {/* Email Field */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address{" "}
                      <span className="text-gray-400">(Optional)</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        updateEmail(e.target.value);
                        validateEmail(e.target.value);
                      }}
                      onBlur={(e) => validateEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className={`w-full p-3 border rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:ring-4 bg-white shadow-sm ${emailError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-100"
                        }`}
                    />
                    {emailError && (
                      <p className="mt-1 text-sm text-red-600">{emailError}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1m-1-1V8a1 1 0 00-1-1H4a1 1 0 00-1 1v8a1 1 0 001 1h1" />
                    </svg>
                  </div>
                  Vehicle Information
                  <span className="text-sm font-normal text-gray-500 ml-2">(Optional - provide this OR upload photos)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Make Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Make
                    </label>
                    <select
                      value={vehicleMake}
                      onChange={(e) => updateVehicleMake(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
                    >
                      <option value="">Select Make</option>
                      {makesLoading ? (
                        <option disabled>Loading makes...</option>
                      ) : makesError ? (
                        <option disabled>Error loading makes</option>
                      ) : (
                        makes.map((make) => (
                          <option key={make.Make_ID} value={make.Make_Name}>
                            {make.Make_Name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Model Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model
                    </label>
                    <input
                      type="text"
                      value={vehicleModel}
                      onChange={(e) => updateVehicleModel(e.target.value)}
                      placeholder="Enter vehicle model"
                      className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
                    />
                  </div>

                  {/* Year Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    <input
                      type="text"
                      value={vehicleYear}
                      onChange={(e) => updateVehicleYear(e.target.value)}
                      placeholder="YYYY"
                      maxLength="4"
                      className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
                    />
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Tip:</strong> If you don't know your vehicle details, you can skip this section and upload photos of your dashboard and VIN sticker instead.
                  </p>
                </div>
              </div>

              {/* Drag and Drop Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-blue-800 text-sm">
                  <strong>💡 Tip:</strong> You can drag and drop images directly
                  onto the upload areas below, or click to select files.
                </div>
              </div>

              {/* Dashboard Photo Upload */}
              <ImageUpload
                title="📸 Dashboard Photo"
                description="Upload a photo of your dashboard showing warning lights or gauges (Optional)"
                inputId="dashboard-upload"
                required={false}
                onImageChange={updateDashboardImage}
              />

              {/* VIN Sticker Photo Upload */}
              <ImageUpload
                title="🏷️ VIN Sticker Photo"
                description="Upload a photo of the VIN sticker in the driver's door (Optional)"
                inputId="vin-upload"
                required={false}
                onImageChange={updateVinImage}
              />

              {/* Problem Description */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  Problem Description
                </h3>
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                  Describe what's happening with your car (e.g., "The check engine
                  light came on and the car shakes when idle")
                </p>
                <textarea
                  value={description}
                  onChange={(e) => updateDescription(e.target.value)}
                  placeholder="Example: The check engine light came on and the car shakes when idle..."
                  className="w-full p-4 border border-gray-300 rounded-lg text-base font-sans resize-y min-h-[120px] transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
                  rows="4"
                  required
                />
              </div>

              {/* Analysis Button */}
              {/* <button
            onClick={handleAnalyze}
            className="w-full max-w-xs mx-auto bg-gradient-to-r from-orange-500 to-red-500 text-white border-none py-4 px-8 rounded-full text-lg font-semibold cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            disabled={loading || !isFormValid()}
          >
            {loading ? "🔄 Analyzing..." : "Analyze"}
          </button> */}

              <button
                className={`${
                  isFormValid() && !loading
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-800 hover:bg-gray-900'
                } text-white border-none py-4 px-8 rounded-full text-lg font-semibold cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none`}
                onClick={handleAnalyze}
                disabled={loading || !isFormValid()}
                style={{
                  width: "100%",
                  maxWidth: "20rem",
                  margin: "0 auto",
                  marginTop: "20px",
                  display: "block",
                  // background: "linear-gradient(to right, #f97316, #ef4444)",
                  color: "white",
                  border: "none",
                  padding: "1rem 2rem",
                  borderRadius: "9999px",
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  cursor: loading || !isFormValid() ? "not-allowed" : "pointer",
                  opacity: loading || !isFormValid() ? 0.7 : 1,
                  transition: "all 0.3s ease",
                  boxShadow: "none",
                  transform: loading || !isFormValid() ? "none" : "translateY(0)",
                }}
                onMouseEnter={(e) => {
                  if (!loading && isFormValid()) {
                    e.currentTarget.style.boxShadow =
                      "0 10px 20px rgba(0,0,0,0.15)";
                    e.currentTarget.style.transform = "translateY(-4px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {loading ? "🔄 Analyzing..." : "Analyze"}
              </button>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mt-6">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <strong>Error:</strong> {error}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DiagnosticResult;
